import { FORM_OPTIONS, LOT_TO_UNIT, formKey } from './constants';
import { checkFormDisabled, checkAccountTypeHasOrderType, getMaxBuy, getMaxSell } from './utils';

/**
 * @return {{
 *  BBCA: {
 *    ...FORM_INITIAL_STATE,
 *  },
 *  BBNI: {
 *    ...FORM_INITIAL_STATE,
 *  },
 * }} data format
 */
export const INITIAL_STATE = {};

export const FORM_INITIAL_STATE = (formType) => ({
  // Form Config
  [formKey.CONST_FORM_TYPE]: formType,
  // General Order Details
  [formKey.INPUT_ORDER_SIDE]: FORM_OPTIONS.ORDER_SIDE[0],
  [formKey.INPUT_ACCOUNT_TYPE]: FORM_OPTIONS.ACCOUNT_TYPE[0],
  [formKey.TAB_ORDER_TYPE]: FORM_OPTIONS.ORDER_TYPE[0],
  [formKey.INPUT_ORDER_PERIOD]: FORM_OPTIONS.ORDER_PERIOD[0],
});

export const formAction = {
  // Update Stock form
  SETUP_FORM_CREATE: 'SETUP_FORM_CREATE',
  SETUP_FORM_AMEND: 'SETUP_FORM_AMEND',
  SET_CONST_FROM_API: 'SET_CONST_FROM_API',
  CHANGE_INPUT_FROM_API: 'CHANGE_INPUT_FROM_API',
  CHANGE_ORDER_SIDE: 'CHANGE_ORDER_SIDE',
  CHANGE_ACCOUNT_TYPE: 'CHANGE_ACCOUNT_TYPE',
  CHANGE_ORDER_TYPE: 'CHANGE_ORDER_TYPE',
  CHANGE_ORDER_PERIOD: 'CHANGE_ORDER_PERIOD',
  CHANGE_PRICE: 'CHANGE_PRICE',
  CHANGE_LOT_COUNT: 'CHANGE_LOT_COUNT',
  CHANGE_BTN_PERCENTAGE: 'CHANGE_BTN_PERCENTAGE',
  CHANGE_AUTO_START_DATE: 'CHANGE_AUTO_START_DATE',
  CHANGE_AUTO_END_DATE: 'CHANGE_AUTO_END_DATE',
  CHANGE_THRESHOLD_COMPARATOR: 'CHANGE_THRESHOLD_COMPARATOR',
  CHANGE_THRESHOLD_VALUE: 'CHANGE_THRESHOLD_VALUE',
  // Reset data
  RESET_STOCK_FORM: 'RESET_STOCK_FORM',
  RESET_ALL_FORM: 'RESET_ALL_FORM',
};

const StockBuySellReducer = (state, action) => {
  const { type, code, payload } = action;
  switch (type) {
    case formAction.SETUP_FORM_CREATE:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          ...FORM_INITIAL_STATE('CREATE'),
        },
      };
    case formAction.SETUP_FORM_AMEND:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          ...FORM_INITIAL_STATE('AMEND'),
        },
      };
    case formAction.SET_CONST_FROM_API:
    case formAction.CHANGE_INPUT_FROM_API:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          ...payload,
        },
      };
    case formAction.CHANGE_ORDER_SIDE: {
      // Check whether the combination of next order side + current account type is allowed
      const { isDisabled, callback } = checkFormDisabled({
        orderSide: payload,
        accountType: state[code][formKey.INPUT_ACCOUNT_TYPE]?.value,
        disabledForms: state[code][formKey.CONST_DISABLED_FORMS],
      });
      if (isDisabled) {
        // If not allowed, call callback & return unchanged state
        if (callback) callback();
        return state;
      }

      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_ORDER_SIDE]: payload,
        },
      };
    }
    case formAction.CHANGE_ACCOUNT_TYPE: {
      // Check whether the combination of next account type + current order side is allowed
      const { isDisabled, callback } = checkFormDisabled({
        orderSide: state[code][formKey.INPUT_ORDER_SIDE],
        accountType: payload?.value,
        disabledForms: state[code][formKey.CONST_DISABLED_FORMS],
      });
      if (isDisabled) {
        // If not allowed, call callback & return unchanged state
        if (callback) callback();
        return state;
      }

      // Check whether new account type has current order type
      // If not, replace with default account type
      const { newOrderType } = checkAccountTypeHasOrderType({
        newAccountType: payload,
        currentOrderType: state[code]?.[formKey.TAB_ORDER_TYPE],
      });
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_ACCOUNT_TYPE]: payload,
          [formKey.TAB_ORDER_TYPE]: newOrderType,
        },
      };
    }
    case formAction.CHANGE_ORDER_TYPE:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.TAB_ORDER_TYPE]: payload,
        },
      };
    case formAction.CHANGE_ORDER_PERIOD:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_ORDER_PERIOD]: payload,
        },
      };
    case formAction.CHANGE_PRICE: {
      const newPrice = payload;
      const currentLotCount = state[code]?.[formKey.INPUT_LOT_COUNT] ?? 0;
      const newTotal = newPrice * currentLotCount * LOT_TO_UNIT;
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.BTN_PERCENTAGE]: null,
          [formKey.INPUT_PRICE]: newPrice,
          [formKey.CALC_ORDER_TOTAL]: newTotal,
        },
      };
    }
    case formAction.CHANGE_LOT_COUNT: {
      const newLotCount = payload;
      const currentPrice = state[code]?.[formKey.INPUT_PRICE] ?? 0;
      const newTotal = currentPrice * newLotCount * LOT_TO_UNIT;
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.BTN_PERCENTAGE]: null,
          [formKey.INPUT_LOT_COUNT]: newLotCount,
          [formKey.CALC_ORDER_TOTAL]: newTotal,
        },
      };
    }
    case formAction.CHANGE_BTN_PERCENTAGE: {
      const newBtnPercentage = payload;
      const currentPrice =
        state[code]?.[formKey.INPUT_PRICE] ?? state[code]?.[formKey.CONST_DEFAULT_PRICE] ?? 0;
      let newLotCount;
      let newTotal;
      switch (state[code]?.[formKey.INPUT_ORDER_SIDE]) {
        case 'BUY': {
          const maxTotal = getMaxBuy(state[code], formKey);
          const _newTotal = newBtnPercentage * maxTotal;
          newLotCount = Math.floor(_newTotal / currentPrice / LOT_TO_UNIT);
          newTotal = currentPrice * newLotCount * LOT_TO_UNIT;
          break;
        }
        case 'SELL': {
          const maxLotCount = getMaxSell(state[code], formKey);
          newLotCount = Math.floor(newBtnPercentage * maxLotCount);
          newTotal = currentPrice * newLotCount * LOT_TO_UNIT;
          break;
        }
        default:
          break;
      }
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_PRICE]: currentPrice,
          [formKey.BTN_PERCENTAGE]: newBtnPercentage,
          [formKey.INPUT_LOT_COUNT]: newLotCount,
          [formKey.CALC_ORDER_TOTAL]: newTotal,
        },
      };
    }
    case formAction.CHANGE_AUTO_START_DATE:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_START_DATE]: payload,
        },
      };
    case formAction.CHANGE_AUTO_END_DATE:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_END_DATE]: payload,
        },
      };
    case formAction.CHANGE_THRESHOLD_COMPARATOR:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_THRESHOLD_COMPARATOR]: payload,
        },
      };
    case formAction.CHANGE_THRESHOLD_VALUE:
      return {
        ...state,
        [code]: {
          ...(state[code] || {}),
          [formKey.INPUT_THRESHOLD_VALUE]: payload,
        },
      };
    case formAction.RESET_STOCK_FORM:
      return {
        ...state,
        [code]: {
          // Retain API data
          ...(state[code] || {}),
          // Reset form data
          [formKey.INPUT_ORDER_SIDE]: FORM_INITIAL_STATE[formKey.INPUT_ORDER_SIDE],
          [formKey.INPUT_ACCOUNT_TYPE]: FORM_INITIAL_STATE[formKey.INPUT_ACCOUNT_TYPE],
          [formKey.TAB_ORDER_TYPE]: FORM_INITIAL_STATE[formKey.TAB_ORDER_TYPE],
          [formKey.INPUT_ORDER_PERIOD]: FORM_INITIAL_STATE[formKey.INPUT_ORDER_PERIOD],
          [formKey.INPUT_PRICE]: FORM_INITIAL_STATE[formKey.INPUT_PRICE],
          [formKey.INPUT_LOT_COUNT]: FORM_INITIAL_STATE[formKey.INPUT_LOT_COUNT],
          [formKey.BTN_PERCENTAGE]: FORM_INITIAL_STATE[formKey.BTN_PERCENTAGE],
          [formKey.CALC_ORDER_TOTAL]: FORM_INITIAL_STATE[formKey.CALC_ORDER_TOTAL],
        },
      };
    case formAction.RESET_ALL_FORM:
      return { ...INITIAL_STATE };
    default:
      return state;
  }
};

export default StockBuySellReducer;
